import { startTransition, useState } from 'react';
import * as yup from 'yup';
import axios from 'Adapter';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Col, FormGroup, Label, Row } from 'reactstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { api } from 'Utils';
import { usePrompt } from 'Hooks';
import { AspectRatio, EmployeeForm, Media } from 'Types';
import {
	Card,
	FormSuccess,
	SubmitButtonGroup,
	SwitchController,
	AvatarController
} from 'Elements';

export const CreateEmployee = () => {
	const defaultImage = {
		absoluteUri: '',
		thumbnail: ''
	};
	const descriptionLimit = 270;
	const { t } = useTranslation();
	const [image, setImage] = useState<Partial<Media>>(defaultImage);
	const [formSuccess, setFormSuccess] = useState(false);

	const schema = yup.object({
		fullName: yup.string().required(t('validation.employee-name.required'))
	});

	const defaultValues: EmployeeForm = {
		image: {
			thumbnail: '',
			url: ''
		},
		fullName: '',
		description: '',
		jobPosition: '',
		isActive: true,
		showInHomePage: false
	};

	const {
		register,
		control,
		reset,
		watch,
		handleSubmit,
		setValue,
		formState: { errors, isDirty, isSubmitting }
	} = useForm<EmployeeForm>({
		resolver: yupResolver(schema),
		defaultValues
	});

	usePrompt(t('forms.leave-screen-massage'), isDirty);

	const resetForm = () => {
		reset(defaultValues);
		setImage(defaultImage);
	};

	const handleImage = (image_data: Partial<Media>) => {
		setImage(image_data);
		setValue(
			'image',
			{
				url: image_data.absoluteUri ?? '',
				thumbnail: image_data.thumbnailUrl ?? ''
			},
			{ shouldDirty: true }
		);
	};

	const submitRequest: SubmitHandler<EmployeeForm> = async (data) => {
		const api_data = {
			...data,
			isActive: true,
			showInHomePage: data?.showInHomePage ? data?.showInHomePage : false,
			image:
				image.absoluteUri === ''
					? null
					: {
							url: image?.absoluteUri,
							thumbnail: image?.thumbnailUrl
					  }
		};
		await axios.post(api.createEmployee, api_data).then((res) => {
			if (res.status === 200) {
				startTransition(() => {
					setFormSuccess(true);
					resetForm();
				});
			}
		});
	};

	const submitHandler: SubmitHandler<EmployeeForm> = async (data) => {
		await submitRequest(data);
	};

	return (
		<Card>
			{!formSuccess && (
				<Row>
					<Col sm={5} xl={4}>
						<FormGroup>
							<AvatarController
								name="image"
								aspectRatio={AspectRatio['3/4']}
								control={control}
								image={image.absoluteUri}
								onRemove={handleImage}
								onUpload={handleImage}
							/>
						</FormGroup>
					</Col>
					<Col sm={7} xl={8}>
						<form onSubmit={handleSubmit(submitHandler)} noValidate>
							<Row>
								<Col xl={9} xxl={7}>
									<FormGroup>
										<Label htmlFor="fullName">
											{t('forms.employee-name')}
											<small className="ms-1">
												({t('validation.required')})
											</small>
										</Label>
										<input
											{...register('fullName')}
											type="text"
											id="fullName"
											aria-invalid={!!errors.fullName}
											className="inputbox w-100"
										/>
										{errors.fullName && (
											<div className="invalid-feedback d-block">
												{errors.fullName.message?.toString()}
											</div>
										)}
									</FormGroup>
									<FormGroup>
										<Label htmlFor="jobPosition">
											{t('forms.job-title')}
										</Label>
										<input
											{...register('jobPosition')}
											type="text"
											id="jobPosition"
											className="inputbox w-100"
										/>
									</FormGroup>
									<FormGroup className="position-relative">
										<Label htmlFor="description">
											{t(
												'forms.business-partner.employee-desc'
											)}
										</Label>
										<textarea
											{...register('description', {
												maxLength: descriptionLimit
											})}
											id="description"
											className="inputbox w-100"
											cols={10}
											rows={5}
											aria-invalid={!!errors.description}
											maxLength={descriptionLimit}
										/>
										<div className="position-absolute end-0">
											<small className="ms-auto mt-1">
												{watch('description')?.length ||
													0}
												/{descriptionLimit}
											</small>
										</div>
									</FormGroup>
									<FormGroup>
										<SwitchController
											name="showInHomePage"
											control={control}
											label="forms.show-in-home"
										/>
									</FormGroup>
									<SubmitButtonGroup
										cancelButton="/employees"
										isDisable={!isDirty}
										isSubmitting={isSubmitting}
									/>
								</Col>
							</Row>
						</form>
					</Col>
				</Row>
			)}
			{formSuccess && (
				<FormSuccess
					addButtonTitle="button.add-employee"
					listTitle="button.employee-list"
					successTitle="forms.employee-created"
					className="flex-grow-1 align-self-center"
					listUrl="/employees"
					onClickAdd={() => {
						setFormSuccess(false);
						resetForm();
					}}
				/>
			)}
		</Card>
	);
};
